import {
  useFilteredEntryCount,
  useFilteredEntryQuery,
} from 'features/drilldown/rolodex/dataHelpers/drilldownDataHelpers'
import { OntologyRolodexEntryType } from 'features/dynamicOntology'
import { usePaginatedQuery } from 'features/fde'
import { useRolodexEntries } from 'features/rolodex'
import { useMemo } from 'react'
import { GeckoEntry } from 'types'

interface UsePaginatedEntriesProps {
  selectedEntryType: OntologyRolodexEntryType
  numberOfEntriesPerPage: number
  currentPage: number
}

interface UsePaginatedEntriesOutput {
  entries: ReadonlyArray<GeckoEntry>
  totalEntries: number
  totalPages: number
  isLoading: boolean
}

export const usePaginatedEntries = ({
  selectedEntryType,
  numberOfEntriesPerPage,
  currentPage,
}: UsePaginatedEntriesProps): UsePaginatedEntriesOutput => {
  const { count: totalEntryCount, isLoading: isLoadingEntryCount } =
    useFilteredEntryCount(selectedEntryType)

  const totalPages = useMemo(
    () => Math.ceil(totalEntryCount / numberOfEntriesPerPage),
    [totalEntryCount, numberOfEntriesPerPage]
  )

  const { query: entryQuery, isLoading: isLoadingBaseEntryQuery } =
    useFilteredEntryQuery(selectedEntryType)

  const {
    query: currentPageQuery,
    isLoading: isLoadingEntryQuery,
    totalEntries,
  } = usePaginatedQuery(
    entryQuery,
    {
      pageSize: numberOfEntriesPerPage,
      currentPage,
    },
    selectedEntryType
  )

  const { entries: currentPageEntries, isLoading: isLoadingEntries } =
    useRolodexEntries(currentPageQuery)

  const entries = currentPageEntries ?? []

  const isLoading =
    isLoadingEntryCount ||
    isLoadingBaseEntryQuery ||
    isLoadingEntryQuery ||
    isLoadingEntries

  return {
    entries,
    totalEntries,
    totalPages,
    isLoading,
  }
}
